import React, { useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { generateAccountStatement } from "api/business-user-management";
import { IGenerateAccountStatementPayload } from "api/business-user-management/types";
import { ReactComponent as DotIconRounded } from "assets/icons/vertical-dot-rounded.svg";
import { useBusinesses } from "contexts/businesses-context";
import { useNavigate } from "react-router-dom";
import { ROUTES_BASE } from "routes/constants";
import { formatDateToYYYYMMDD, handleDownloadBufferData } from "utils";

import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { DropDown } from "ui/dropdown";
import { IDropDownRef } from "ui/dropdown/types";
import {
  DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA,
  GenerateAccountStatementModal,
} from "ui/generate-account-statement-modal";
import { Paragraph3 } from "ui/typography";

import { BUSINESS_SUB_ACCOUNT_NAME_IN_URL_SEPARATOR } from "../../accounts-transactions/const";
import { ApplyPostNoDebitModal } from "./ApplyPostNoDebitModal";
import { CloseAccountModal } from "./CloseAccountModal";
import { RemovePostNoDebitModal } from "./RemovePostNoDebitModal";
import {
  businessAccountDetailDropDownBtnCss,
  businessAccountDetailDropDownPopUpMenuContentCss,
} from "./styles";
import { IBusinessAccountActionsProps } from "./types";

const DROP_DOWN_WIDTH = 240;
const textColorMap: Record<string, string> = {
  regular: "#1A202C",
  danger: "#DC2626",
};

export const BusinessAccountActions = (props: IBusinessAccountActionsProps) => {
  const {
    businessAccount: { accountStatus, walletId, accountName },
    handleGetBusinessAccounts,
  } = props;
  const navigate = useNavigate();
  const { businessDetails } = useBusinesses();
  const dropDownRef = useRef<IDropDownRef | null>(null);
  const [isApplyPostNoDebitModalOpen, setIsApplyPostNoDebitModalOpen] =
    useState(false);
  const [isRemovePostNoDebitModalOpen, setIsRemovePostNoDebitModalOpen] =
    useState(false);
  const [isCloseAccountModalOpen, setIsCloseAccountModalOpen] = useState(false);
  const [
    isGenerateAccountStatementModalOpen,
    setIsGenerateAccountStatementModalOpen,
  ] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState(
    DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA
  );

  const handleToggleApplyPostNoDebitModal = () =>
    setIsApplyPostNoDebitModalOpen(!isApplyPostNoDebitModalOpen);

  const handleToggleRemovePostNoDebitModal = () =>
    setIsRemovePostNoDebitModalOpen(!isRemovePostNoDebitModalOpen);

  const handleToggleCloseAccountModal = () =>
    setIsCloseAccountModalOpen(!isCloseAccountModalOpen);

  const handleToggleGenerateAccountStatementModal = () =>
    setIsGenerateAccountStatementModalOpen(
      !isGenerateAccountStatementModalOpen
    );

  const handleToggleDropDown = () => {
    if (!dropDownRef.current) return;
    dropDownRef.current.handleToggleDropdown();
  };

  const handleGenerateAccountStatement = async () => {
    const { startDate, endDate, recipient } = formData;
    if (
      !businessDetails?.business?.id ||
      recipient === "" ||
      !businessDetails?.business?.email ||
      !startDate ||
      !endDate
    ) {
      return;
    }
    const payload: IGenerateAccountStatementPayload = {
      businessId: businessDetails?.business?.id,
      walletId: String(walletId),
      startDate: formatDateToYYYYMMDD(startDate),
      endDate: formatDateToYYYYMMDD(endDate),
      sendType: recipient,
      email: businessDetails?.business?.email,
    };
    setIsGenerating(true);
    try {
      const { error, message, data } = await generateAccountStatement(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        if (data?.data) {
          handleDownloadBufferData({
            data: data?.data,
            name: `${businessDetails?.business?.companyData?.businessName || "N/A"}'s account statement`,
            type: "pdf",
          });
        }
        customSuccessToast({
          message,
        });
        setFormData(DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA);
        handleToggleGenerateAccountStatementModal();
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsGenerating(false);
  };

  const quickActions = [
    {
      label: "View Transactions",
      isHidden: false,
      handleQuickActionItemClick: () => {
        handleToggleDropDown();
        navigate(
          `${ROUTES_BASE.BUSINESSES}/accounts/${accountName.replace(/ /g, BUSINESS_SUB_ACCOUNT_NAME_IN_URL_SEPARATOR)}/${walletId}/transactions`
        );
      },
      type: "regular",
    },
    {
      label: "Generate Account Statement",
      isHidden: false,
      handleQuickActionItemClick: () => {
        handleToggleDropDown();
        handleToggleGenerateAccountStatementModal();
      },
      type: "regular",
    },
    {
      label: accountStatus === "frozen" ? "Unfreeze" : "Freeze",
      isHidden: false,
      handleQuickActionItemClick: () => {
        handleToggleDropDown();
        if (accountStatus === "frozen") {
          handleToggleRemovePostNoDebitModal();
        } else {
          handleToggleApplyPostNoDebitModal();
        }
      },
      type: "regular",
    },
    {
      label: "Close Account",
      isHidden: false,
      handleQuickActionItemClick: () => {
        handleToggleDropDown();
        handleToggleCloseAccountModal();
      },
      type: "danger",
    },
  ];

  return (
    <>
      <DropDown
        dropDownWidth={DROP_DOWN_WIDTH}
        dropDownBtnContent={
          <DotIconRounded className="w-9 h-9 cursor-pointer" />
        }
        allowOutSideClick
        customDropDownButtonCss={businessAccountDetailDropDownBtnCss}
        dropDownPopUpMenuContentCss={
          businessAccountDetailDropDownPopUpMenuContentCss
        }
        ref={dropDownRef}
      >
        <Box className="flex flex-col">
          {quickActions.map(
            ({ label, isHidden, handleQuickActionItemClick, type }, idx) => {
              return (
                <Box
                  className={`h-10 py-[10px] flex items-center ${isHidden ? "cursor-not-allowed" : "cursor-pointer"}`}
                  key={idx}
                  onClick={handleQuickActionItemClick}
                >
                  <Paragraph3
                    textColor={textColorMap[type]}
                    lineHeight="20px"
                    fontWeight="600"
                  >
                    {label}
                  </Paragraph3>
                </Box>
              );
            }
          )}
        </Box>
      </DropDown>
      <RemovePostNoDebitModal
        isOpen={isRemovePostNoDebitModalOpen}
        setIsOpen={setIsRemovePostNoDebitModalOpen}
        handleToggleRemovePostNoDebitModal={handleToggleRemovePostNoDebitModal}
        handleGetBusinessAccounts={handleGetBusinessAccounts}
        walletId={walletId}
      />
      <ApplyPostNoDebitModal
        isOpen={isApplyPostNoDebitModalOpen}
        setIsOpen={setIsApplyPostNoDebitModalOpen}
        handleToggleApplyPostNoDebitModal={handleToggleApplyPostNoDebitModal}
        handleGetBusinessAccounts={handleGetBusinessAccounts}
        walletId={walletId}
      />
      <CloseAccountModal
        isOpen={isCloseAccountModalOpen}
        setIsOpen={setIsCloseAccountModalOpen}
        handleToggleCloseAccountModal={handleToggleCloseAccountModal}
        handleGetBusinessAccounts={handleGetBusinessAccounts}
        walletId={walletId}
      />
      <GenerateAccountStatementModal
        isOpen={isGenerateAccountStatementModalOpen}
        setIsOpen={setIsGenerateAccountStatementModalOpen}
        handleToggleModal={handleToggleGenerateAccountStatementModal}
        isLoading={isGenerating}
        formData={formData}
        setFormData={setFormData}
        handleGenerateAccountStatement={handleGenerateAccountStatement}
      />
    </>
  );
};
